import { IconArrowLeft } from "@/assets/icons/geist/IconArrowLeft";
import { IconChevronDownSmall } from "@/assets/icons/geist/IconChevronDownSmall";
import { cn } from "@/components/podkit/lib/cn";
import { withGitpodSize } from "@/assets/icons/utils";
import { EditorIcon } from "@/components/environments/EditorIcon";
import { ButtonGroup, ButtonGroupItem } from "@/components/flexkit/ButtonGroup";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import { CopyableInput } from "@/components/podkit/forms/CopyableInput";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuPortal,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { Pill } from "@/components/podkit/status/Pill";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { Heading2 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { Tooltip } from "@/components/Tooltip";
import { useGetEditorForEnv, useSetEditorIdForEnv } from "@/hooks/use-editor-for-env";
import { useOpenEditor } from "@/hooks/use-open-editor";
import { useOpenEditorShortcut } from "@/hooks/use-open-editor-shortcut";
import { TrackLocations } from "@/hooks/use-segment";
import { type PlainEditor } from "@/queries/editor-queries";
import type { PlainEnvironment } from "@/queries/environment-queries";
import { OpenInEditorKeyBinding } from "@/utils/platform";
import IconConsoleSimple from "central-icons/IconConsoleSimple";
import { CheckIcon, ChevronDown } from "lucide-react";
import { useRef, useState, type FC, type ReactElement } from "react";
import { useNavigate } from "react-router-dom";

const IconSSH = withGitpodSize(IconConsoleSimple);

type OpenInEditorButtonProps = {
    environment: PlainEnvironment;
    disabled?: boolean;
};

export const OpenInEditorButton: FC<OpenInEditorButtonProps> = ({ environment, disabled }) => {
    const openEditor = useOpenEditor();
    const {
        selectedEditor,
        selectedEditorVersion,
        isSingleEditor,
        availableEditors,
        isPending: isPendingEditors,
    } = useGetEditorForEnv(environment.id);
    const anchor = useRef<HTMLDivElement>(null);

    // Get the latest version for the selected editor from the server-filtered versions
    // Server returns versions sorted with latest first
    const getLatestAllowedVersion = (editor: PlainEditor): string | undefined => {
        return editor.versions?.[0]?.version;
    };

    useOpenEditorShortcut((triggered) => {
        if (triggered && selectedEditor) {
            void openEditor(
                selectedEditor,
                environment,
                selectedEditorVersion || getLatestAllowedVersion(selectedEditor),
            );
        }
    });

    const tooltip: string | ReactElement = disabled ? (
        "Start environment to open in editor"
    ) : (
        <div className="flex flex-row items-center gap-2">
            Open editor
            <KeyboardCombo keys={OpenInEditorKeyBinding} className="ml-1" />
        </div>
    );

    return (
        <div data-testid="open-in-default-editor" ref={anchor}>
            <SkeletonBlock ready={!!selectedEditor && !isPendingEditors}>
                {selectedEditor && (
                    <ButtonGroup variant="secondary">
                        <Tooltip content={tooltip} usePortal={true}>
                            <ButtonGroupItem
                                onClick={() =>
                                    openEditor(
                                        selectedEditor,
                                        environment,
                                        selectedEditorVersion || getLatestAllowedVersion(selectedEditor),
                                    )
                                }
                                disabled={disabled}
                                LeadingIcon={(_props) => (
                                    <EditorIcon editor={selectedEditor} size="sm" className="shrink-0" />
                                )}
                                data-tracking-id="open-in-selected-editor"
                            >
                                <span>
                                    Open <span className="hidden @xs:inline">{selectedEditor.name}</span>
                                </span>
                            </ButtonGroupItem>
                        </Tooltip>
                        {!isSingleEditor && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <ButtonGroupItem
                                        data-testid="open-editor-selection-popover"
                                        LeadingIcon={IconChevronDownSmall}
                                        aria-label="Expand editor selection"
                                        disabled={disabled}
                                    />
                                </DropdownMenuTrigger>
                                <DropdownMenuPortal>
                                    <DropdownMenuContent
                                        align="end"
                                        className="w-auto min-w-64 transition-all duration-200"
                                        data-track-location={TrackLocations.EnvironmentEditorPopover}
                                    >
                                        <EditorSelectionPopover
                                            environment={environment}
                                            editors={availableEditors || []}
                                        />
                                    </DropdownMenuContent>
                                </DropdownMenuPortal>
                            </DropdownMenu>
                        )}
                    </ButtonGroup>
                )}
            </SkeletonBlock>
        </div>
    );
};

type EditorSelectionPopoverProps = {
    environment: PlainEnvironment;
    editors: PlainEditor[];
    onClose?: () => void;
};
const EditorSelectionPopover: FC<EditorSelectionPopoverProps> = ({ environment, editors }) => {
    const navigate = useNavigate();
    const { selectedEditor, selectedEditorVersion } = useGetEditorForEnv(environment.id);
    const [showSSH, setShowSSH] = useState(false);

    return (
        <div
            className={cn(
                "relative overflow-hidden transition-[width] duration-200 ease-in-out",
                showSSH ? "w-128" : "w-64",
            )}
        >
            {/* Editor selection view */}
            <div
                className={`flex w-64 flex-col transition-all duration-200 ease-in-out ${
                    showSSH ? "-translate-x-full opacity-0" : "translate-x-0 opacity-100"
                }`}
                style={{ display: showSSH ? "none" : "flex" }}
            >
                <SkeletonBlock ready={!!editors}>
                    {editors.map((editor) => (
                        <EditorMenuItem
                            key={editor.id}
                            editor={editor}
                            environment={environment}
                            isSelected={editor.id === selectedEditor?.id}
                            selectedEditorVersion={selectedEditorVersion}
                        />
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup>
                        <DropdownMenuLabel>Other options</DropdownMenuLabel>
                        <DropdownMenuItem
                            onSelect={(e) => {
                                e.preventDefault();
                                setShowSSH(true);
                            }}
                            data-tracking-id="open-ssh-from-editor-dropdown"
                            data-testid="open-ssh"
                            LeadingIcon={IconSSH}
                        >
                            SSH
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onClick={() => navigate(`/settings/preferences`)}
                            data-tracking-id="change-default-editor"
                        >
                            Change default editor
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onClick={() => window.open("https://ona.com/docs/ona/editors/overview", "_blank")}
                            data-tracking-id="troubleshoot-editor"
                        >
                            Troubleshoot
                        </DropdownMenuItem>
                    </DropdownMenuGroup>
                </SkeletonBlock>
            </div>

            {/* SSH view */}
            <div
                className={`transition-all duration-200 ease-in-out ${
                    showSSH ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
                }`}
                style={{ display: showSSH ? "block" : "none" }}
            >
                <SSHContent environment={environment} onBack={() => setShowSSH(false)} />
            </div>
        </div>
    );
};

type SSHContentProps = {
    environment: PlainEnvironment;
    onBack: () => void;
};
const SSHContent: FC<SSHContentProps> = ({ environment, onBack }) => {
    return (
        <div className="flex w-128 flex-col gap-4 p-4">
            <button
                onClick={onBack}
                data-tracking-id="back-from-ssh"
                className="flex items-center gap-1 text-sm text-content-secondary hover:text-content-primary"
            >
                <IconArrowLeft size="sm" />
                Back
            </button>
            <div>
                <Heading2>Connect via SSH</Heading2>
                <Text muted className="text-base">
                    How to connect via SSH from your local machine using the CLI
                </Text>
            </div>
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                    <p className="text-sm font-bold">Setup SSH host config</p>
                    <CopyableInput value={"gitpod env ssh-config"} data-tracking-id="copy-ssh-config-command" />
                </div>
                <div className="flex flex-col gap-1">
                    <p className="text-sm font-bold">Connect to dev container</p>
                    <CopyableInput
                        value={`ssh ${environment.id}.gitpod.environment`}
                        data-tracking-id="copy-ssh-connect-command"
                    />
                </div>
            </div>
            <div className="-mx-1 my-1 h-px bg-content-tertiary/20" />
            <p className="text-sm text-content-secondary">Documentation</p>
            <div className="flex flex-col gap-2">
                <div>
                    <ExternalLink href="https://ona.com/docs/ona/integrations/cli">Install the CLI</ExternalLink>
                </div>
                <div>
                    <ExternalLink href="https://ona.com/docs/ona/integrations/cli#ssh-configuration">
                        SSH Configuration
                    </ExternalLink>
                </div>
            </div>
        </div>
    );
};

type EditorMenuItemProps = {
    editor: PlainEditor;
    environment: PlainEnvironment;
    isSelected: boolean;
    selectedEditorVersion?: string;
};

const EditorMenuItem: FC<EditorMenuItemProps> = ({ editor, environment, isSelected, selectedEditorVersion }) => {
    const openEditor = useOpenEditor();
    const setEditorForEnv = useSetEditorIdForEnv(environment.id);

    // Show version picker if editor has MORE THAN ONE version available
    const shouldShowVersions = editor.versions.length > 1;
    if (shouldShowVersions) {
        return (
            <EditorWithVersions
                editor={editor}
                environment={environment}
                selectedEditorVersion={selectedEditorVersion}
            />
        );
    }

    return (
        <DropdownMenuItem
            onSelect={() => {
                void openEditor(editor, environment, undefined);
                void setEditorForEnv(editor.id, undefined);
            }}
            aria-label={`Open in ${editor.name}`}
            aria-current={isSelected ? "true" : undefined}
            LeadingIcon={(_props) => <EditorIcon editor={editor} size="sm" className="shrink-0" />}
        >
            <span className="min-w-0 flex-1 truncate">{editor.name}</span>
            {isSelected && (
                <div className="flex items-center justify-center gap-2 p-0.5 pl-2">
                    <CheckIcon className="size-4" aria-hidden="true" />
                </div>
            )}
        </DropdownMenuItem>
    );
};

type EditorWithVersionsProps = {
    editor: PlainEditor;
    environment: PlainEnvironment;
    selectedEditorVersion?: string;
};
const EditorWithVersions: FC<EditorWithVersionsProps> = ({ editor, environment, selectedEditorVersion }) => {
    const openEditor = useOpenEditor();
    const setEditorForEnv = useSetEditorIdForEnv(environment.id);
    const effectiveVersion = selectedEditorVersion ?? editor.versions[0]?.version;

    const menuItemRef = useRef<HTMLDivElement>(null);
    const submenuTriggerRef = useRef<HTMLDivElement>(null);

    return (
        <DropdownMenuSub>
            {/* Row container with hover styling */}
            <div
                className={cn(
                    "relative flex cursor-default select-none items-center rounded text-sm",
                    "cursor-pointer",
                    "mx-1 h-8 text-base",
                    "group",
                )}
            >
                {/* Main clickable area - opens the displayed version */}
                <DropdownMenuItem
                    className={cn(
                        "flex h-full flex-1 items-center gap-2 py-1.5 pl-2 pr-1",
                        "rounded-l rounded-r-none focus:outline-none",
                        "mx-0 min-w-0 hover:bg-transparent focus:bg-surface-hover",
                    )}
                    onSelect={() => {
                        void openEditor(editor, environment, effectiveVersion);
                        void setEditorForEnv(editor.id, effectiveVersion);
                    }}
                    ref={menuItemRef}
                    onKeyDown={(e) => {
                        if (e.key === "ArrowRight") {
                            e.preventDefault();
                            submenuTriggerRef.current?.focus();
                        }
                    }}
                    aria-label={`Open in ${editor.name} ${effectiveVersion}`}
                    data-testid={`editor-open-${editor.id}`}
                >
                    <EditorIcon editor={editor} size="sm" className="shrink-0" />
                    <span className="min-w-0 flex-1 truncate text-left">{editor.name}</span>
                    <Pill size="sm" variant="neutral">
                        {effectiveVersion}
                    </Pill>
                </DropdownMenuItem>

                {/* Chevron trigger - only this element triggers the submenu */}
                <DropdownMenuSubTrigger
                    ref={submenuTriggerRef}
                    onKeyDown={(e) => {
                        if (e.key === "ArrowLeft") {
                            e.preventDefault();
                            menuItemRef.current?.focus();
                        }
                    }}
                    className={cn(
                        "flex h-full cursor-default items-center justify-center rounded-l-none rounded-r pl-1.5 pr-2",
                        "focus:bg-surface-hover focus:outline-none",
                        "[&>svg:last-child]:hidden", // Hide the default chevron from DropdownMenuSubTrigger
                        "mx-0", // Override inherited margin
                    )}
                    aria-label={`Select ${editor.name} version`}
                    data-testid={`editor-version-trigger-${editor.id}`}
                >
                    <ChevronDown className="size-4 text-content-secondary" />
                </DropdownMenuSubTrigger>
            </div>

            <DropdownMenuPortal>
                {/* side offset was added for working around https://github.com/shadcn-ui/ui/issues/9275 */}
                <DropdownMenuSubContent sideOffset={5}>
                    {editor.versions.map((version) => {
                        const isSelectedVersion = effectiveVersion === version.version;
                        return (
                            <DropdownMenuItem
                                key={version.version}
                                onSelect={() => {
                                    void openEditor(editor, environment, version.version);
                                    void setEditorForEnv(editor.id, version.version);
                                }}
                            >
                                <span className="flex flex-1 items-center gap-2">
                                    <span>{version.version}</span>
                                </span>
                                {isSelectedVersion && (
                                    <div className="ml-2 flex items-center justify-center gap-2 p-0.5">
                                        <CheckIcon className="size-4 text-content-brand" aria-hidden="true" />
                                    </div>
                                )}
                            </DropdownMenuItem>
                        );
                    })}
                </DropdownMenuSubContent>
            </DropdownMenuPortal>
        </DropdownMenuSub>
    );
};
